import React, { useState } from 'react';
import axios from 'axios';
import { format, addDays, addMonths, addYears } from 'date-fns';
import { Calendar, X, Users, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * BulkValidityModal — set validity for many users at once.
 *
 * Designed to solve the exact rollout question the admin asked: "give the
 * admin the option to choose expiration date for all existing users". So
 * the default `target` is `all_no_expiry` (= legacy users without an expiry
 * yet) — the safest bulk action.
 */
const BULK_TARGETS = [
  {
    id: 'all_no_expiry',
    label: 'Existing users without an expiry (recommended)',
    hint: "Sets a date on legacy users — won't touch trial users or anyone already extended.",
  },
  {
    id: 'all_expired',
    label: 'All currently-expired users',
    hint: 'Re-grants access to users whose validity is already in the past.',
  },
  {
    id: 'trial_users',
    label: 'Current trial users',
    hint: 'Users still inside their 7-day signup trial window.',
  },
  {
    id: 'all',
    label: 'EVERY non-admin user',
    hint: 'Use with care — overwrites the validity of every member.',
  },
];

const BulkValidityModal = ({ onClose, onSaved }) => {
  const [target, setTarget] = useState('all_no_expiry');
  const [customDate, setCustomDate] = useState(
    format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
  );
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  const presets = [
    { label: '+30 days', fn: () => addDays(new Date(), 30) },
    { label: '+90 days', fn: () => addMonths(new Date(), 3) },
    { label: '+1 year',  fn: () => addYears(new Date(), 1) },
  ];

  const apply = async ({ valid_until = null, clear = false }) => {
    setSaving(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const body = { target, ...(clear ? { clear: true } : { valid_until }) };
      const { data } = await axios.post(
        `${API}/admin/users/validity/bulk`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data);
      toast({
        title: 'Bulk validity updated',
        description: `${data.modified} user(s) updated.`,
      });
      onSaved?.();
    } catch (err) {
      toast({
        title: 'Bulk update failed',
        description: err?.response?.data?.detail || err?.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreset = (p) => apply({ valid_until: p.fn().toISOString() });
  const handleCustom = (e) => {
    e.preventDefault();
    if (!customDate) return;
    apply({ valid_until: new Date(`${customDate}T23:59:59`).toISOString() });
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="bulk-validity-modal"
    >
      <div className="bg-zinc-950 border border-white/10 rounded-2xl max-w-lg w-full p-6 text-white shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5 text-fuchsia-400" /> Bulk set validity
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Apply an expiry to many users in one shot.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/5 text-gray-400 hover:text-white"
            aria-label="Close"
            data-testid="bulk-validity-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Target picker */}
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Who?</p>
          <div className="space-y-2">
            {BULK_TARGETS.map((t) => (
              <label
                key={t.id}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  target === t.id
                    ? 'bg-fuchsia-500/10 border-fuchsia-400/40'
                    : 'bg-white/5 border-white/10 hover:border-white/30'
                }`}
                data-testid={`bulk-target-${t.id}`}
              >
                <input
                  type="radio"
                  name="bulk-target"
                  value={t.id}
                  checked={target === t.id}
                  onChange={() => setTarget(t.id)}
                  className="mt-0.5 accent-fuchsia-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">{t.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t.hint}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Result banner */}
        {result && (
          <div className="mb-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-200">
            <CheckCircle2 className="inline h-4 w-4 mr-1.5 -mt-0.5" />
            Matched <span className="font-semibold">{result.matched}</span>,{' '}
            updated <span className="font-semibold">{result.modified}</span>.
            {result.valid_until && (
              <> Valid until <span className="font-semibold">{format(new Date(result.valid_until), 'MMM d, yyyy')}</span>.</>
            )}
          </div>
        )}

        {/* Presets */}
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Set expiry</p>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                disabled={saving}
                onClick={() => handlePreset(p)}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-200 disabled:opacity-50"
                data-testid={`bulk-preset-${p.label.replace(/\s+/g, '-').replace('+', 'plus')}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date */}
        <form onSubmit={handleCustom} className="mb-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Or pick exact date</p>
          <div className="flex gap-2">
            <Input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={format(new Date(), 'yyyy-MM-dd')}
              className="flex-1 bg-zinc-900 border-white/10 text-white"
              data-testid="bulk-validity-custom-date"
            />
            <Button
              type="submit"
              disabled={saving || !customDate}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              data-testid="bulk-validity-apply"
            >
              Apply to {target.replace(/_/g, ' ')}
            </Button>
          </div>
        </form>

        {/* Clear button — opt-in, requires confirm because it grants free access */}
        <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-start gap-1.5 text-xs text-amber-300/90">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Granting lifetime access removes the expiry for everyone matching the target.</span>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (window.confirm(`Grant LIFETIME access to all "${target.replace(/_/g, ' ')}" users?`)) {
                apply({ clear: true });
              }
            }}
            className="text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-gray-200 disabled:opacity-40 flex-shrink-0"
            data-testid="bulk-validity-clear"
          >
            Lifetime
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkValidityModal;
